// Copyright (c) Docugami, Inc. All rights reserved.

import { Config } from "config";
import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { createDgBaseQuery } from "store/baseQuery";
import { AssistedAuthoringChunkInfo } from "networking/api";
import { createEntityAdapter, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit";
import { AppState } from "store";
import { Clause, GetClausesResponse, SaveClauseRequest, ClauseLabel, UpdateClauseLabelRequest, HideClausesRequest, DocumentHiddenClauses } from "networking/assistedAuthoringApi";
import { DgKnownChunk } from "components/doc_hosting/doc_hosts/word/v3/DgParagraphMetadata";

const aaChunkInfoAdapter = createEntityAdapter<AssistedAuthoringChunkInfo>({
    selectId: x => x.spanId
});

export const {
    selectAll: selectAllAAChunkInfos
} = aaChunkInfoAdapter.getSelectors();

const clauseLabelAdapter = createEntityAdapter<ClauseLabel>({
    selectId: x => x.id
});

export const {
    selectAll: selectAllClauseLabels
} = clauseLabelAdapter.getSelectors();

const clauseAdapter = createEntityAdapter<Clause>({
    selectId: x => x.id
});

export const {
    selectAll: selectAllClauses
} = clauseAdapter.getSelectors();

export interface ClausesState {
    clauses: EntityState<Clause>;
    clauseLabels: EntityState<ClauseLabel>;
}

type WithDocSetId<T> = T & { docSetId: string };
type WithDocumentId<T> = T & { documentId: string };

const assistedAuthBaseQuery = createDgBaseQuery(Config.reportsApiUrl);
export const assistedAuthoringApi = createApi({
    reducerPath: 'assistedAuthoringApi',
    tagTypes: ["clauses", "clauseSuggestions"],
    baseQuery: assistedAuthBaseQuery,
    endpoints: builder => ({
        fetchAssistedAuthoringInfos: builder.query<EntityState<AssistedAuthoringChunkInfo>, string>({
            query: (documentId) => `/files/${encodeURIComponent(documentId)}/aa-infos`,
            transformResponse: (response: AssistedAuthoringChunkInfo[]): EntityState<AssistedAuthoringChunkInfo> => {
                return aaChunkInfoAdapter.setAll(aaChunkInfoAdapter.getInitialState(), response)
            }
        }),
        getAllClauses: builder.query<ClausesState, string>({
            query: (docSetId) => `/clause-library/clusters/${encodeURIComponent(docSetId)}/clauses`,
            providesTags: (result, error, arg) => [{ type: 'clauses', id: arg }],
            transformResponse: (response: GetClausesResponse): ClausesState => {
                return {
                    clauses: clauseAdapter.setAll(clauseAdapter.getInitialState(), response.clauses),
                    clauseLabels: clauseLabelAdapter.setAll(clauseLabelAdapter.getInitialState(), response.clauseLabels)
                }
            }
        }),
        saveClause: builder.mutation<Clause, WithDocSetId<SaveClauseRequest>>({
            query: request => ({
                url: `/clause-library/clusters/${encodeURIComponent(request.docSetId)}/clause`,
                method: 'POST',
                body: request
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'clauses', id: arg.docSetId }]
        }),
        updateClauseLabel: builder.mutation<ClauseLabel, WithDocSetId<UpdateClauseLabelRequest>>({
            query: request => ({
                url: `/clause-library/clusters/${encodeURIComponent(request.docSetId)}/labels`,
                method: 'PUT',
                body: request
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'clauses', id: arg.docSetId }]
        }),
        removeClause: builder.mutation<void, {docSetId: string, clauseId: string}>({
            query: request => ({
                url: `/clause-library/clusters/${encodeURIComponent(request.docSetId)}/clauses/${encodeURIComponent(request.clauseId)}`,
                method: 'DELETE'
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'clauses', id: arg.docSetId }]
        }),
        removeClauseLabel: builder.mutation<void, {docSetId: string, clauseLabelId?: string}>({
            query: request => ({
                url: `/clause-library/clusters/${encodeURIComponent(request.docSetId)}/clauses`,
                method: 'DELETE',
                params: !!request.clauseLabelId ? { labelId: request.clauseLabelId } : undefined
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'clauses', id: arg.docSetId }]
        }),
        getHiddenClauseSuggestions: builder.query<DocumentHiddenClauses, string>({
            query: (documentId) => `/hidden-clause-suggestions/documents/${encodeURIComponent(documentId)}/`,
            providesTags: (result, error, arg) => [{ type: 'clauseSuggestions', id: arg }]
        }),
        hideClauses: builder.mutation<DocumentHiddenClauses, WithDocumentId<HideClausesRequest>>({
            query: request => ({
                url: `/hidden-clause-suggestions/documents/${encodeURIComponent(request.documentId)}/`,
                method: 'POST',
                body: request
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'clauseSuggestions', id: arg.documentId }]
        }),
        unhideClauses: builder.mutation<DocumentHiddenClauses, WithDocumentId<HideClausesRequest>>({
            query: request => ({
                url: `/hidden-clause-suggestions/documents/${encodeURIComponent(request.documentId)}/`,
                method: 'DELETE',
                body: request
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'clauseSuggestions', id: arg.documentId }]
        })
    })
});

export const selectGetAssistedAuthoringChunkInfos = (state: AppState, folderId: string) => assistedAuthoringApi.endpoints.fetchAssistedAuthoringInfos.select(folderId)(state)?.data;
export const selectGetAllClauses = (state: AppState, clusterId: string) => assistedAuthoringApi.endpoints.getAllClauses.select(clusterId)(state)?.data;
export const selectGetHiddenClauseSuggestions = (state: AppState, documentId: string) => assistedAuthoringApi.endpoints.getHiddenClauseSuggestions.select(documentId)(state)?.data;

export const {
    useFetchAssistedAuthoringInfosQuery,
    useGetAllClausesQuery,
    useSaveClauseMutation,
    useUpdateClauseLabelMutation,
    useRemoveClauseMutation,
    useRemoveClauseLabelMutation,
    useGetHiddenClauseSuggestionsQuery,
    useHideClausesMutation,
    useUnhideClausesMutation
} = assistedAuthoringApi

interface AssistedAuthoringInfoState {
    knownChunks: DgKnownChunk[];
}

const initialState: AssistedAuthoringInfoState = {
    knownChunks: []
};

export const assistedAuthoringSlice = createSlice({
    name: 'assistedAuthoringInfo',
    initialState: initialState,
    reducers: {
        setKnownChunks(state, action: PayloadAction<DgKnownChunk[]>) {
            state.knownChunks = [...action.payload];
        }
    }
})

export const { setKnownChunks } = assistedAuthoringSlice.actions;
